/**
 * /leave/admin — HR view (PR 4b, ADR 0008).
 *
 * Three tabs:
 *   - All requests   — every leave request in the org, with filters
 *   - Balance corrections — HR records a ledger adjustment with reason
 *   - Backdated leave   — admin/owner only; records past-date approved leave
 */
import { createRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { authedLayoutRoute } from './authed.js';
import {
  listLeaveRequests,
  postBackdatedLeave,
  postBalanceCorrection,
} from '../api/leave.js';
import { ApiError } from '../api/client.js';
import { Alert } from '../components/Alert.js';
import { useAuth } from '../auth/AuthContext.js';
import { formatDate } from '../lib/format.js';

export const leaveAdminRoute = createRoute({
  getParentRoute: () => authedLayoutRoute,
  path: '/leave/admin',
  component: LeaveAdminPage,
});

const LEAVE_TYPES = [
  'annual',
  'sick',
  'maternity',
  'paternity',
  'compassionate',
  'unpaid',
  'other',
] as const;

function LeaveAdminPage(): JSX.Element {
  const { user } = useAuth();
  const isHr = user?.role === 'owner' || user?.role === 'admin' || user?.role === 'hr';
  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const [tab, setTab] = useState<'all' | 'corrections' | 'backdated'>('all');

  if (!isHr) {
    return (
      <section className="p-8 max-w-3xl">
        <Alert variant="error">Restricted to HR / admin / owner roles.</Alert>
      </section>
    );
  }

  return (
    <section className="p-8 space-y-6 max-w-4xl">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">Leave admin</h1>
        <p className="text-sm text-gray-400">
          Org-wide leave management: see every request, record balance
          corrections, and (admin/owner only) enter backdated leave.
        </p>
      </header>
      <nav className="flex gap-2 border-b border-gray-900">
        <Tab active={tab === 'all'} onClick={() => setTab('all')}>
          All requests
        </Tab>
        <Tab active={tab === 'corrections'} onClick={() => setTab('corrections')}>
          Balance corrections
        </Tab>
        {isAdmin ? (
          <Tab active={tab === 'backdated'} onClick={() => setTab('backdated')}>
            Backdated leave
          </Tab>
        ) : null}
      </nav>
      {tab === 'all' ? <AllRequestsPanel /> : null}
      {tab === 'corrections' ? <CorrectionsPanel /> : null}
      {tab === 'backdated' && isAdmin ? <BackdatedPanel /> : null}
    </section>
  );
}

function Tab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 text-sm border-b-2 ${
        active
          ? 'border-white text-white'
          : 'border-transparent text-gray-400 hover:text-white'
      }`}
    >
      {children}
    </button>
  );
}

function AllRequestsPanel(): JSX.Element {
  const query = useQuery({
    queryKey: ['leave', 'requests', 'all'],
    queryFn: () => listLeaveRequests({ view: 'all' }),
  });
  if (query.isLoading) return <Placeholder>Loading…</Placeholder>;
  if (query.isError) return <Placeholder error>Could not load.</Placeholder>;
  const requests = query.data?.requests ?? [];
  if (requests.length === 0)
    return <Placeholder>No leave requests in this organization yet.</Placeholder>;
  return (
    <div className="rounded border border-gray-900 overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-gray-950 text-xs uppercase tracking-wide text-gray-500">
          <tr>
            <th className="text-left px-4 py-2">Employee</th>
            <th className="text-left px-4 py-2">Type</th>
            <th className="text-left px-4 py-2">Dates</th>
            <th className="text-left px-4 py-2">Days</th>
            <th className="text-left px-4 py-2">Status</th>
            <th className="text-left px-4 py-2">Backdated?</th>
          </tr>
        </thead>
        <tbody>
          {requests.map((r) => (
            <tr key={r.id} className="border-t border-gray-900">
              <td className="px-4 py-3 text-gray-300">
                {r.employeeFirstName} {r.employeeLastName}{' '}
                <span className="text-gray-500 font-mono text-xs">{r.employeeNumber}</span>
              </td>
              <td className="px-4 py-3 text-gray-400">{r.leaveType.replace(/_/g, ' ')}</td>
              <td className="px-4 py-3 font-mono text-xs text-gray-300">
                {formatDate(r.startDate)} → {formatDate(r.endDate)}
              </td>
              <td className="px-4 py-3 font-mono">{r.workingDaysCount.toFixed(1)}</td>
              <td className="px-4 py-3 text-gray-400">{r.status}</td>
              <td className="px-4 py-3 text-gray-500">{r.isBackdated ? 'Yes' : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const CorrectionSchema = z.object({
  employeeId: z.string().min(1, 'Required'),
  leaveType: z.enum(LEAVE_TYPES),
  dayDelta: z.coerce
    .number()
    .refine((v) => v !== 0, { message: 'Must be non-zero' }),
  reason: z.string().min(10, 'Reason must be at least 10 chars'),
});
type CorrectionValues = z.infer<typeof CorrectionSchema>;

function CorrectionsPanel(): JSX.Element {
  const queryClient = useQueryClient();
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } =
    useForm<CorrectionValues>({
      resolver: zodResolver(CorrectionSchema),
      defaultValues: { employeeId: '', leaveType: 'annual', dayDelta: 0, reason: '' },
    });
  const mut = useMutation({
    mutationFn: postBalanceCorrection,
    onSuccess: async () => {
      setSuccess('Correction recorded.');
      setError(null);
      reset({ employeeId: '', leaveType: 'annual', dayDelta: 0, reason: '' });
      await queryClient.invalidateQueries({ queryKey: ['leave'] });
    },
    onError: (err: unknown) => {
      setSuccess(null);
      setError(err instanceof ApiError ? err.message : 'Failed.');
    },
  });
  return (
    <form
      onSubmit={handleSubmit((v) =>
        mut.mutate({
          employeeId: v.employeeId,
          leaveType: v.leaveType,
          dayDelta: v.dayDelta,
          reason: v.reason,
        }),
      )}
      className="rounded border border-gray-900 p-4 space-y-3 max-w-xl"
      noValidate
    >
      <p className="text-xs text-gray-500">
        Records a ledger entry (kind: correction). Use a positive dayDelta
        to credit, negative to debit. Reason is required — it lives in the
        audit log.
      </p>
      {success ? (
        <div className="rounded border border-green-900 bg-green-950/40 px-3 py-2 text-sm text-green-300">
          {success}
        </div>
      ) : null}
      <label className="block text-xs uppercase text-gray-400">
        Employee ID
        <input
          {...register('employeeId')}
          placeholder="cm…"
          className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm font-mono"
        />
        {errors.employeeId ? (
          <span className="text-xs text-red-400">{errors.employeeId.message}</span>
        ) : null}
      </label>
      <label className="block text-xs uppercase text-gray-400">
        Leave type
        <select
          {...register('leaveType')}
          className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm"
        >
          {LEAVE_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-xs uppercase text-gray-400">
        Day delta (+ credit / − debit)
        <input
          type="number"
          step="0.5"
          {...register('dayDelta')}
          className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm font-mono"
        />
        {errors.dayDelta ? (
          <span className="text-xs text-red-400">{errors.dayDelta.message}</span>
        ) : null}
      </label>
      <label className="block text-xs uppercase text-gray-400">
        Reason (audit-required, min 10 chars)
        <textarea
          {...register('reason')}
          rows={3}
          className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm"
        />
        {errors.reason ? (
          <span className="text-xs text-red-400">{errors.reason.message}</span>
        ) : null}
      </label>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <button
        type="submit"
        disabled={isSubmitting || mut.isPending}
        className="rounded bg-white text-black text-sm font-medium px-4 py-2 disabled:opacity-50"
      >
        {mut.isPending ? 'Recording…' : 'Record correction'}
      </button>
    </form>
  );
}

const BackdatedSchema = z.object({
  employeeId: z.string().min(1, 'Required'),
  leaveType: z.enum(LEAVE_TYPES),
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD'),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD'),
  startDayPart: z.enum(['full', 'morning_half', 'afternoon_half']),
  endDayPart: z.enum(['full', 'morning_half', 'afternoon_half']),
  reason: z.string().min(5, 'Reason required (min 5 chars)'),
});
type BackdatedValues = z.infer<typeof BackdatedSchema>;

function BackdatedPanel(): JSX.Element {
  const queryClient = useQueryClient();
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } =
    useForm<BackdatedValues>({
      resolver: zodResolver(BackdatedSchema),
      defaultValues: {
        employeeId: '',
        leaveType: 'annual',
        startDate: '',
        endDate: '',
        startDayPart: 'full',
        endDayPart: 'full',
        reason: '',
      },
    });
  const mut = useMutation({
    mutationFn: postBackdatedLeave,
    onSuccess: async (r) => {
      setSuccess(`Backdated leave recorded — ${r.workingDaysCount} day(s) debited.`);
      setError(null);
      reset();
      await queryClient.invalidateQueries({ queryKey: ['leave'] });
    },
    onError: (err: unknown) => {
      setSuccess(null);
      setError(err instanceof ApiError ? err.message : 'Failed.');
    },
  });
  return (
    <form
      onSubmit={handleSubmit((v) => mut.mutate(v))}
      className="rounded border border-gray-900 p-4 space-y-3 max-w-xl"
      noValidate
    >
      <p className="text-xs text-gray-500">
        Admin / owner only. Records leave for a past date range — bypasses
        the normal &ldquo;no past dates&rdquo; check. Reason required;
        audit log flags this as <code>leave_request.backdated_create</code>.
      </p>
      {success ? (
        <div className="rounded border border-green-900 bg-green-950/40 px-3 py-2 text-sm text-green-300">
          {success}
        </div>
      ) : null}
      <label className="block text-xs uppercase text-gray-400">
        Employee ID
        <input
          {...register('employeeId')}
          className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm font-mono"
        />
        {errors.employeeId ? (
          <span className="text-xs text-red-400">{errors.employeeId.message}</span>
        ) : null}
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs uppercase text-gray-400">
          Leave type
          <select
            {...register('leaveType')}
            className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm"
          >
            {LEAVE_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
        <div />
        <label className="text-xs uppercase text-gray-400">
          Start date
          <input
            type="date"
            {...register('startDate')}
            className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm"
          />
        </label>
        <label className="text-xs uppercase text-gray-400">
          End date
          <input
            type="date"
            {...register('endDate')}
            className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm"
          />
        </label>
        <label className="text-xs uppercase text-gray-400">
          Start day
          <select
            {...register('startDayPart')}
            className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm"
          >
            <option value="full">Full day</option>
            <option value="morning_half">Morning only</option>
            <option value="afternoon_half">Afternoon only</option>
          </select>
        </label>
        <label className="text-xs uppercase text-gray-400">
          End day
          <select
            {...register('endDayPart')}
            className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm"
          >
            <option value="full">Full day</option>
            <option value="morning_half">Morning only</option>
            <option value="afternoon_half">Afternoon only</option>
          </select>
        </label>
      </div>
      <label className="block text-xs uppercase text-gray-400">
        Reason (audit-required)
        <textarea
          {...register('reason')}
          rows={3}
          className="mt-1 w-full rounded border border-gray-700 bg-gray-900 px-3 py-2 text-white text-sm"
        />
        {errors.reason ? (
          <span className="text-xs text-red-400">{errors.reason.message}</span>
        ) : null}
      </label>
      {error ? <Alert variant="error">{error}</Alert> : null}
      <button
        type="submit"
        disabled={isSubmitting || mut.isPending}
        className="rounded bg-white text-black text-sm font-medium px-4 py-2 disabled:opacity-50"
      >
        {mut.isPending ? 'Recording…' : 'Record backdated leave'}
      </button>
    </form>
  );
}

function Placeholder({
  children,
  error,
}: {
  children: React.ReactNode;
  error?: boolean;
}): JSX.Element {
  return (
    <div
      className={`rounded border p-6 text-center text-sm ${
        error
          ? 'border-red-900 bg-red-950/40 text-red-300'
          : 'border-gray-900 text-gray-500'
      }`}
    >
      {children}
    </div>
  );
}
